import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Divider, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackTwoTone';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { confirmAsync } from '@components/Modals/ConfirmPopup';
import { unsavedChangesMessage } from '@forms/types';
import usePageTitle from '@hooks/usePageTitle';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import useUserTerminals from '@hooks/useUserTerminals';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { queueUncontrolledToast } from '@toast';
import { queueModal } from '@components/Modals/modalSlice';
import { selectAppFeatures } from '@/Infrastructure/Utilities/appSelectors';
import { useCreateBoardQueryMutation, useFetchBoardQuery, useUpdateBoardQueryMutation } from '@api/Trinium/Endpoints/BoardQueries/slice';
import { cloneQueryConfiguration, defaultAllOpenQuery, isActiveQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import { BoardQuery } from '@api/Trinium/types';
import ApplicationSpinner from '@/Infrastructure/Components/Loading/ApplicationSpinner';
import QueryList from './QueryList';
import QueryEditor from './QueryEditor';
import QueryPreview from './QueryPreview';
import { BoardQueryFormFields, copyOfName, getBoardQueryValidationSchema, toFormFields } from './types';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';

import styles from './Page.module.css';

/**
 * Editor for the Dispatch Board's dynamic queries. Reached from the Edit button on the board toolbar,
 * which passes the query that was selected at the time via the 'query' search parameter.
 */
export default function BoardQueriesPage() {
  const { addPageTitle } = usePageTitle();
  const { number: terminalNumber } = useCurrentTerminal();
  const { terminals } = useUserTerminals();
  const { data: user } = useFetchMyProfileQuery(undefined);
  const featureFlags = useAppSelector((state) => selectAppFeatures(state));
  const appDispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [createBoardQuery] = useCreateBoardQueryMutation();
  const [updateBoardQuery] = useUpdateBoardQueryMutation();
  const [updateActiveState, { isLoading: isUpdatingActiveState, originalArgs: activeStateArgs }] = useUpdateBoardQueryMutation();

  const queryName = searchParams.get('query');

  const { data, isLoading } = useFetchBoardQuery(terminalNumber);

  /** Query being created, which has no server record yet. While one is open it is what the page is editing. */
  const [draftQuery, setDraftQuery] = useState<BoardQuery>();

  /** Drafts take a negative id, which keys their Formik instance without ever colliding with a saved query's id. */
  const nextDraftId = useRef(0);

  /**
   * The synthetic ALL UNDELIVERED query (id 0) is prepended client-side and has no server record, so it cannot be edited.
   * Disabled queries sink below the active ones so the list leads with what is actually on the board.
   */
  const editableQueries = useMemo(
    () =>
      (data ?? [])
        .filter((query) => query.id > 0)
        .sort(
          (left, right) =>
            Number(!isActiveQuery(left)) - Number(!isActiveQuery(right)) ||
            left.name.localeCompare(right.name, 'en', { sensitivity: 'base' })
        ),
    [data]
  );

  const savedQuery = useMemo(() => editableQueries.find((query) => query.name === queryName), [editableQueries, queryName]);

  const selectedQuery = draftQuery ?? savedQuery;

  const isNewQuery = Boolean(draftQuery);

  /** Names a new query has to stay clear of, which includes the ALL UNDELIVERED the board resolves client-side. */
  const existingNames = useMemo(() => (data ?? []).map((query) => query.name), [data]);

  const validationSchema = useMemo(() => getBoardQueryValidationSchema(isNewQuery, existingNames), [isNewQuery, existingNames]);

  useEffect(() => {
    addPageTitle('Edit Board Queries');
  }, [addPageTitle]);

  // The title bar sits outside the Formik tree, so the guard reaches dirty through the instance rather than context
  const formikRef = useRef<FormikProps<BoardQueryFormFields>>(null);

  /** An open draft is unsaved work even before it is edited, since a copy arrives complete and savable as it stands. */
  const hasUnsavedWork = () => Boolean(formikRef.current?.dirty) || isNewQuery;

  const handleBack = async () => {
    if (hasUnsavedWork() && !(await confirmAsync(unsavedChangesMessage))) return;

    // A query disabled here is no longer routable, so the board falls back to the user's default instead
    void navigate(
      queryName && savedQuery && isActiveQuery(savedQuery)
        ? `/${terminalNumber}/Board/${encodeURIComponent(queryName)}`
        : `/${terminalNumber}/Board`
    );
  };

  const selectQueryName = (name: string) => {
    setSearchParams(
      (previous) => {
        previous.set('query', name);
        return previous;
      },
      { replace: true }
    );
  };

  const handleSelect = (query: BoardQuery) => {
    setDraftQuery(undefined);
    selectQueryName(query.name);
  };

  /** Opens a query the server has no record of, configured from the query it is being started from. */
  const openDraft = async (source: BoardQuery, name: string) => {
    if (hasUnsavedWork() && !(await confirmAsync(unsavedChangesMessage))) return;

    nextDraftId.current -= 1;

    // isDefault and isFavorite are the user's own marks on the source query, so a copy does not carry them over.
    setDraftQuery({ id: nextDraftId.current, name, configuration: cloneQueryConfiguration(source.configuration) });
  };

  const handleCancelDraft = async () => {
    if (formikRef.current?.dirty && !(await confirmAsync(unsavedChangesMessage))) return;

    setDraftQuery(undefined);
  };

  const handleSubmit = async (values: BoardQueryFormFields, helpers: FormikHelpers<BoardQueryFormFields>) => {
    if (!selectedQuery) return;

    const configuration = {
      ...selectedQuery.configuration,
      columns: values.columns,
      pinnedCols: { ...selectedQuery.configuration.pinnedCols, left: values.pinnedFields },
      filters: values.filters
    };

    try {
      if (isNewQuery) {
        const created = await createBoardQuery({ terminalNumber, query: { name: values.name.trim(), configuration } }).unwrap();

        // The query has a server record now, so the URL takes the selection back over and the form reloads from it
        setDraftQuery(undefined);
        selectQueryName(created.name);
      } else {
        await updateBoardQuery({ terminalNumber, query: { ...selectedQuery, name: values.name, configuration } }).unwrap();

        // Settles the form against what was just saved so the Save button reads as having no pending changes
        helpers.resetForm({ values });
      }

      appDispatch(
        queueUncontrolledToast({
          type: 'message',
          message: isNewQuery ? 'Board query created' : 'Board query saved',
          severity: 'success',
          autoCloseMs: 3000
        })
      );
    } catch {
      appDispatch(
        queueUncontrolledToast({
          type: 'message',
          message: `Unable to ${isNewQuery ? 'create' : 'save'} the board query, if the problem persists please contact IT Support.`,
          severity: 'error',
          autoCloseMs: 5000
        })
      );
    }
  };

  /**
   * Sends the query as last saved rather than the form's values, so pending edits are neither saved nor lost by the
   * toggle — the form is keyed on the query id, so the cache update does not remount it.
   */
  const handleToggleActive = async () => {
    if (!savedQuery) return;

    const isActive = !isActiveQuery(savedQuery);

    try {
      await updateActiveState({ terminalNumber, query: { ...savedQuery, isActive } }).unwrap();

      appDispatch(
        queueUncontrolledToast({
          type: 'message',
          message: isActive ? 'Board query enabled' : 'Board query disabled',
          severity: 'success',
          autoCloseMs: 3000
        })
      );
    } catch {
      appDispatch(
        queueUncontrolledToast({
          type: 'message',
          message: `Unable to ${isActive ? 'enable' : 'disable'} the board query, if the problem persists please contact IT Support.`,
          severity: 'error',
          autoCloseMs: 5000
        })
      );
    }
  };

  /** When this feature is not enabled than the user should not see this page */
  if (!featureFlags?.editBoardQueries || !user?.canModifyBoardQueries) {
    return (
      <Navigate
        to='/'
        replace
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles['title-bar']}>
        <span className={styles.title}>Edit Board Queries</span>

        <IconButton
          title='Return to the Dispatch Board'
          onClick={() => {
            void handleBack();
          }}
          className={`${styles['icon-btn']} ${styles['back-btn']}`}>
          <ArrowBackIcon />
        </IconButton>
      </div>

      {isLoading ? (
        <ApplicationSpinner height={'70%'} />
      ) : (
        <Formik
          innerRef={formikRef}
          // Remounting on the selected query is what loads its values. Reinitializing instead would tie the form to the
          // fetchBoard cache, and a failed save rolling that cache back would take the user's pending edits with it.
          key={selectedQuery?.id}
          initialValues={selectedQuery ? toFormFields(selectedQuery) : { name: '', columns: [], pinnedFields: [], filters: [] }}
          validationSchema={validationSchema}
          // A draft is submittable before it is touched, so its errors have to exist before the first edit to hold Create back
          validateOnMount={isNewQuery}
          onSubmit={handleSubmit}>
          <>
            <div className={styles.body}>
              <QueryList
                queries={editableQueries}
                selectedQuery={selectedQuery}
                draftQuery={draftQuery}
                onSelect={handleSelect}
                onCreate={() => {
                  void openDraft(defaultAllOpenQuery, '');
                }}
              />

              <Divider
                orientation='vertical'
                flexItem
              />

              {selectedQuery ? (
                <QueryEditor
                  isNewQuery={isNewQuery}
                  isActive={isActiveQuery(selectedQuery)}
                  isUpdatingActiveState={isUpdatingActiveState && activeStateArgs?.query.id === selectedQuery.id}
                  onToggleActive={() => {
                    void handleToggleActive();
                  }}
                  onCopy={() => {
                    void openDraft(selectedQuery, copyOfName(selectedQuery.name, existingNames));
                  }}
                  onCopyToSite={
                    terminals.length > 1
                      ? () => {
                          appDispatch(queueModal({ Component: 'copyQueryToSite', Props: { query: selectedQuery } }));
                        }
                      : undefined
                  }
                  onCancel={() => {
                    void handleCancelDraft();
                  }}
                />
              ) : (
                <span className={styles['editor-empty']}>Select a Board Query to edit its configuration.</span>
              )}
            </div>

            {selectedQuery && (
              <>
                <Divider />

                <QueryPreview query={selectedQuery} />
              </>
            )}
          </>
        </Formik>
      )}
    </div>
  );
}
